import type { ReactNode } from 'react';
import { Button } from '@clay-run/glaze/button';
import { Text } from '@clay-run/glaze/text';
import { CaretDown } from '@phosphor-icons/react/dist/icons/CaretDown';
import { CaretUp } from '@phosphor-icons/react/dist/icons/CaretUp';

/**
 * `PlaySectionCard` — plan `2026-09-25-002-plays-sections-unify-accordion-
 * plan.md` U8/D8/R13/R14, revised per PM QA feedback (2026-09-25): the
 * Glaze `CollapsibleCard` chrome (rounded `border`, `bg-bg-primary`, a p-6
 * header box, and `CollapsibleCardBody`'s baked-in `border-t` divider) wasted
 * space around a table that already carries its own rounded border. This is
 * now a plain accordion — no card wrapper at all. The header is a bare row
 * (chevron + bold title + count); the body, when open, is an unpadded `div`
 * so the table's own toolbar and grid sit directly beneath the header with
 * only an 8px gap (`gap-2`) between them. The 24px gap BETWEEN the three
 * sections lives one level up, in `PlaysSubtab`'s `JOURNEY_SECTION_CLS`
 * (`gap-6`) — unchanged by this revision.
 *
 * Still a controlled accordion (`open`/`onOpenChange` owned by `PlaysSubtab`'s
 * `PlayPeopleJourney`, D8 — page-local `useState`, reset per play via
 * `key={playId}`). The trigger is a real `Button` (`variant="ghost"`) whose
 * own `aria-expanded` carries the state and its content (title + count) names
 * the section — the bare "Expand"/"Collapse" a shared `CollapsibleCardHeader`
 * trigger would produce is ambiguous across the three section accordions on
 * one page.
 *
 * The body is only rendered while `open` — a closed section's
 * `ContactFlowTable` is fully unmounted, not just hidden. Its working state
 * survives the remount through the `contact_play_*` view-state row —
 * `useViewStateMachine` restores it even when the section's
 * `view_<stateKey>` URL param is still present (plan 2026-09-25-003 U1).
 *
 * `asPageHeading` is for a section that is ALSO a page-level heading (the
 * Plays roster 'People'); the three play-people sections never set it, so the
 * page keeps exactly one level-1 heading. The h1 wraps the toggle button
 * (WAI-ARIA APG accordion header): its accessible name is the title (plus
 * count), read from content. The toggle carries no `aria-label` — expanded
 * state is conveyed by `aria-expanded` alone.
 */
export interface PlaySectionCardProps {
  'data-testid'?: string;
  title: string;
  /** `undefined`/`null` renders no count at all — e.g. a section not on the current plays page. */
  count?: number | null;
  /** Appended after the count with a space, e.g. `"4 suggested"`. Omit for a bare count, e.g. `"7"`. */
  countSuffix?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Render the title row inside an `h1`; see the doc comment above. */
  asPageHeading?: boolean;
  /** When set, the title renders as a plain `<span>` carrying exactly this class (no Glaze `Text` classes) — e.g. the Plays roster's A6 heading style. */
  titleClassName?: string;
}

export function PlaySectionCard({ 'data-testid': dataTestId, title, count, countSuffix, open, onOpenChange, children, asPageHeading, titleClassName }: PlaySectionCardProps) {
  const countLabel = count == null ? null : countSuffix ? `${count} ${countSuffix}` : `${count}`;

  const button = (
    <Button
      variant="ghost"
      size="content"
      aria-expanded={open}
      className="flex flex-1 min-w-0 items-center gap-2 justify-start text-left"
      onClick={() => onOpenChange(!open)}
    >
      {open ? <CaretUp size={14} aria-hidden="true" /> : <CaretDown size={14} aria-hidden="true" />}
      {titleClassName ? (
        <span className={titleClassName}>{title}</span>
      ) : (
        <Text render={<span />} size="sm" weight="semibold" className="min-w-0 truncate">{title}</Text>
      )}
      {countLabel !== null && (
        <>
          {' '}
          <Text render={<span />} size="xs" variant="tertiary">{countLabel}</Text>
        </>
      )}
    </Button>
  );

  return (
    <div data-testid={dataTestId} className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        {asPageHeading ? <h1 className="flex flex-1 min-w-0 items-center">{button}</h1> : button}
      </div>
      {open && <div>{children}</div>}
    </div>
  );
}
